'use client';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { IoCloseSharp } from 'react-icons/io5';

import cn from '@/lib/clsxm';
import { getCookie, setCookie } from '@/lib/cookies';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import Banner from '@/components/app/side-in/components/banner';
import BannerWithText from '@/components/app/side-in/components/banner-with-text';
import { useGetSideIn } from '@/components/app/side-in/services/hooks';

import { useSmartOfferStore } from '@/store/smart-offer.store';
import { useTourStore } from '@/store/tour.store';

import { DATA_CY_SIDEIN, DATA_CY_SIDEIN_CLOSE_BTN } from '@/constant/cypress';
import { SIDE_IN_TYPES } from '@/models/side-in.model';
const SIDE_IN_COOKIE_KEY = 'hide-sidein';
export default function SideIn() {
  const [showSideIn, setShowSideIn] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const isSmallScreen = useClientSmallScreen();
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const path = usePathname();
  const isProductPage = /^\/web\/[^/]+(\/(comments|support))?$/.test(path);
  const { setShowSideInComponent } = useSmartOfferStore();
  const { showTour } = useTourStore();
  const [shouldHideSideIn, setShouldHideSideIn] = useState(false);
  const [useAnimationDelay, setUseAnimationDelay] = useState(true);
  const { data } = useGetSideIn();
  const exitAfter = data?.exitAfter || 10000;
  useEffect(() => {
    const cookie = getCookie(SIDE_IN_COOKIE_KEY);
    setShouldHideSideIn(!!cookie);
  }, []);
  useEffect(() => {
    // Trigger the animation after the component mounts
    if (window && data && data.link && data.banner?.publicLink) {
      setShowSideInComponent(true);
      setUseAnimationDelay(true);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, [data]);

  useEffect(() => {
    if (!data || !data?.link || !data?.banner?.publicLink) {
      setShowSideInComponent(false);
    }
  }, [data]);

  useEffect(() => {
    if (getCookie(SIDE_IN_COOKIE_KEY)) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setUseAnimationDelay(true);
    setShowSideIn(true);
    setShowSideInComponent(true);
    setIsVisible(false);
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [path]);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      hideSideIn();
    }, exitAfter);

    return () => clearTimeout(timer);
  }, [isVisible, exitAfter, showSideIn]);

  useEffect(() => {
    if (!isVisible) return;

    const initialScrollY = window.scrollY;

    const SCROLL_THRESHOLD = 600;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolledDistance = Math.abs(currentScrollY - initialScrollY);

      if (scrolledDistance >= SCROLL_THRESHOLD) {
        hideSideIn();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);
  const handleCloseSideIn = () => {
    // Calculate the expiration date for 24 hours
    const maxAgeSeconds = 24 * 60 * 60;
    setCookie(SIDE_IN_COOKIE_KEY, true, { maxAge: maxAgeSeconds });
    setShouldHideSideIn(true);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    setShowSideInComponent(false);
    setShowSideIn(false);
  };

  const hideSideIn = () => {
    setUseAnimationDelay(false);
    setIsVisible(false);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setShowSideIn(false);
      setShowSideInComponent(false);
    }, 750);
  };

  if (!data || !data?.link || !data?.banner?.publicLink) {
    return null;
  }

  if (shouldHideSideIn || showTour) {
    return null;
  }

  return showSideIn ? (
    <Container
      data-cy={DATA_CY_SIDEIN}
      id='zhksidein'
      className={cn(
        useAnimationDelay ? 'delay-[1000ms]' : 'delay-0',
        'fixed -right-[300px] bottom-[40px] z-60 h-auto shadow-[0px_20px_60px_-15px_#00000026] transition-all duration-700 ease-in-out md:bottom-[50px]',
        isProductPage && 'bottom-[100px]',
        data.type !== SIDE_IN_TYPES.IMAGE &&
          !isSmallScreen &&
          '-right-[370px] h-[340px] w-[363px]! rounded-md bg-[#FFFFFF] p-2.5',
        isVisible && 'right-5',
      )}
      style={
        isSmallScreen
          ? { width: `${data?.width}px`, height: 'auto' }
          : data.type === SIDE_IN_TYPES.IMAGE
            ? { width: '300px' }
            : {}
      }
    >
      {data.type === SIDE_IN_TYPES.IMAGE || isSmallScreen ? (
        <Banner link={data.link || '#'} image={data?.banner} />
      ) : (
        <BannerWithText {...data} />
      )}
      <Container
        style={{ backgroundColor: data?.closeButtonBackground }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#E5E8EB';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = data?.closeButtonBackground;
        }}
        className='absolute -top-7 right-0 cursor-pointer rounded-xs p-1 transition duration-300 md:-top-4'
        data-cy={DATA_CY_SIDEIN_CLOSE_BTN}
        onClick={handleCloseSideIn}
      >
        <IoCloseSharp fill={data.closeIconBackground} size={20} />
      </Container>
    </Container>
  ) : null;
}
